import { apiUrl } from "./utils";
import { fetchOrThrow, throwAppError } from "./errors";
import config from "./config";
import type { Attachment, Notification } from "./types";

/**
 * /api/v2 클라이언트(apps/server/ntfy/PLAN.md "v2 API 계약"). 같은 오리진이라 중앙 세션 쿠키가 그대로 가고
 * nginx 가 확인한다 — Authorization 헤더는 없다.
 */

/** v2 메시지 JSON (서버 core.v2_json) */
export interface V2Message {
  id: string;
  seq: string;
  time: number;
  event: string;
  topic: string;
  expires?: number;
  title?: string;
  message?: string;
  tags?: string[];
  actions?: Notification["actions"];
  attachments?: Attachment[];
  encoding?: string;
}

export interface V2Me {
  user: string;
  role: string;
  subscriptions: { topic: string; display_name?: string; muted_until?: number; base_url?: string; created?: number; icon?: string }[];
  prefs: Record<string, unknown>;
}

export interface V2Token {
  token: string;
  label: string;
  expires: number;
  last_access: number;
  last_origin: string;
  created: number;
}

export interface V2Config {
  attachment_file_limit: number;
  attachments_per_message: number;
  attachment_expiry_seconds: number;
  message_expiry_seconds: number;
  message_size_limit: number;
}

/**
 * 발행 필드 — multipart 텍스트 필드 이름 그대로. v2 는 이것만 받는다(서버 v2.V2_FIELDS, 그 밖의 필드는 400):
 * 필수 topic·title·message, 선택 tags·actions(view)·seq + files. click·icon·markdown·delay·URL 첨부·중요도는 없다(2026-10-07).
 */
export interface PublishFields {
  topic: string;
  title: string;
  message: string;
  tags?: string;
}

export type PublishRequest = Promise<V2Message> & { abort: () => void };

/** v2 발행 본문 — multipart 하나(2026-10-07 결정). 빈 값은 보내지 않는다(서버가 title·message 필수를 확인). */
const publishForm = (fields: PublishFields, files: File[]): FormData => {
  const form = new FormData();
  (["topic", "title", "message", "tags"] as const).forEach((k) => {
    const v = fields[k];
    if (v) form.append(k, v);
  });
  files.forEach((f) => form.append("files", f, f.name));
  return form;
};

export const absolute = (url: string | undefined) => (url && url.startsWith("/") ? `${config.base_url}${url}` : url);

/** v2 메시지 → 로컬(Dexie) 알림 행. 시퀀스 id 는 기존 코드가 쓰는 sequence_id 로, 첨부 URL 은 절대 URL 로. */
export const fromV2 = (m: V2Message): Notification => {
  const attachments = (m.attachments ?? []).map((a) => ({ ...a, url: absolute(a.url), thumb: absolute(a.thumb) }));
  const n: Notification = {
    id: m.id,
    time: m.time,
    event: m.event,
    topic: m.topic,
    expires: m.expires,
    sequence_id: m.seq,
  };
  if (m.event !== "message") {
    return n;
  }
  return {
    ...n,
    title: m.title || undefined,
    message: m.message || "",
    tags: m.tags?.length ? m.tags : undefined,
    actions: m.actions?.length ? m.actions : undefined,
    attachments,
    attachment: attachments[0],
  };
};

const jsonInit = (method: string, body?: unknown): RequestInit => ({
  method,
  credentials: "same-origin",
  headers: body === undefined ? {} : { "Content-Type": "application/json" },
  body: body === undefined ? undefined : JSON.stringify(body),
});

class Api {
  configCache: V2Config | null = null;

  async poll(topics: string[], since: string | null | undefined): Promise<Notification[]> {
    const params = new URLSearchParams({ topics: topics.join(",") });
    if (since) {
      params.set("since", since);
    }
    const response = await fetchOrThrow(apiUrl(`/messages?${params}`), { credentials: "same-origin" });
    const body: { messages: V2Message[] } = await response.json();
    return body.messages.map(fromV2);
  }

  streamUrl(topics: string[], since: string | null | undefined) {
    const params = new URLSearchParams({ topics: topics.join(",") });
    if (since) {
      params.set("since", since);
    }
    return apiUrl(`/stream?${params}`);
  }

  /** 글만 빠르게(테스트 메시지·빠른 보내기) — 제목·본문 둘 다 필수 */
  async publishText(topic: string, title: string, message: string, tags?: string[]) {
    // v2 발행은 multipart 하나로만 받는다 — Content-Type 은 브라우저가 boundary 와 함께 채운다
    await fetchOrThrow(apiUrl("/messages"), { method: "POST", body: publishForm({ topic, title, message, tags: tags?.join(",") }, []) });
  }

  /**
   * 발행 — 항상 multipart(파일은 `files` 여러 개, 없어도 multipart). 진행률 때문에 XHR 을 쓴다(fetch 엔 업로드 진행률이 없다).
   */
  publish(fields: PublishFields, files: File[], onProgress?: (ev: ProgressEvent) => void): PublishRequest {
    const xhr = new XMLHttpRequest();
    const promise = new Promise<V2Message>((resolve, reject) => {
      xhr.open("POST", apiUrl("/messages"));
      xhr.withCredentials = true;
      const body = publishForm(fields, files);
      if (onProgress) {
        xhr.upload.addEventListener("progress", onProgress);
      }
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status <= 299) {
          resolve(JSON.parse(xhr.responseText));
          return;
        }
        let detail = `HTTP ${xhr.status}`;
        try {
          const err = JSON.parse(xhr.responseText);
          detail = typeof err.detail === "string" ? err.detail : detail;
        } catch {
          // 본문이 JSON 이 아니다(nginx 413 등)
        }
        reject(new Error(xhr.status === 413 ? `파일이 너무 큽니다 (${detail})` : detail));
      });
      xhr.addEventListener("error", () => reject(new Error("네트워크 오류")));
      xhr.addEventListener("abort", () => reject(new Error("보내기를 취소했습니다")));
      xhr.send(body);
    }) as PublishRequest;
    promise.abort = () => xhr.abort();
    return promise;
  }

  async markNotificationRead(topic: string, sequenceId: string) {
    await fetchOrThrow(apiUrl(`/messages/${topic}/${sequenceId}/read`), jsonInit("PUT"));
  }

  async deleteNotification(topic: string, sequenceId: string) {
    await fetchOrThrow(apiUrl(`/messages/${topic}/${sequenceId}`), jsonInit("DELETE"));
  }

  async me(): Promise<V2Me> {
    const response = await fetchOrThrow(apiUrl("/me"), { credentials: "same-origin" });
    return response.json();
  }

  async putSubscription(topic: string, data: Record<string, unknown> = {}) {
    await fetchOrThrow(apiUrl(`/me/subscriptions/${topic}`), jsonInit("PUT", data));
  }

  /** 구독 아이콘(사진) — 서버가 가운데 정사각 256px 로 잘라 저장한다 */
  async putSubscriptionIcon(topic: string, file: File) {
    const form = new FormData();
    form.append("file", file, file.name);
    await fetchOrThrow(apiUrl(`/me/subscriptions/${topic}/icon`), { method: "PUT", body: form, credentials: "same-origin" });
  }

  async deleteSubscriptionIcon(topic: string) {
    await fetchOrThrow(apiUrl(`/me/subscriptions/${topic}/icon`), jsonInit("DELETE"));
  }

  async deleteSubscription(topic: string) {
    await fetchOrThrow(apiUrl(`/me/subscriptions/${topic}`), jsonInit("DELETE"));
  }

  async patchPrefs(prefs: Record<string, unknown>) {
    await fetchOrThrow(apiUrl("/me/prefs"), jsonInit("PATCH", prefs));
  }

  async tokens(): Promise<V2Token[]> {
    const response = await fetchOrThrow(apiUrl("/tokens"), { credentials: "same-origin" });
    return (await response.json()).tokens;
  }

  async createToken(label: string, expires: number): Promise<V2Token> {
    const response = await fetchOrThrow(apiUrl("/tokens"), jsonInit("POST", { label, expires }));
    return response.json();
  }

  async deleteToken(token: string) {
    await fetchOrThrow(apiUrl(`/tokens/${encodeURIComponent(token)}`), jsonInit("DELETE"));
  }

  async config(): Promise<V2Config> {
    if (!this.configCache) {
      const response = await fetch(apiUrl("/config"), { credentials: "same-origin" });
      if (response.status !== 200) {
        await throwAppError(response);
      }
      this.configCache = await response.json();
    }
    return this.configCache!;
  }
}

const api = new Api();
export default api;
